import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import type { ChatMessage } from "@/domain/aiAgent";
import agentAvatar from "@/assets/agent_ai.png";
import { AiAgentMessageBubble } from "./AiAgentMessageBubble";

interface AiAgentMessageListProps {
  messages: ChatMessage[];
  isLoading: boolean;
  onExampleClick: (question: string) => void;
  onSubmitFeedback: (
    messageId: string,
    rating: "positive" | "negative",
    reason?: string,
    comment?: string,
  ) => void;
}

export const AiAgentMessageList = ({
  messages,
  isLoading,
  onExampleClick,
  onSubmitFeedback,
}: AiAgentMessageListProps) => {
  const { t } = useTranslation();
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  if (messages.length === 0 && !isLoading) {
    const examples = [t("agent.example_1"), t("agent.example_2"), t("agent.example_3")];

    return (
      <div className="ai-chat-empty">
        <div className="ai-chat-empty-avatar">
          <img
            src={agentAvatar}
            alt="AI Assistant"
            className="w-14 h-14 rounded-full object-cover"
          />
        </div>
        <h3 className="ai-chat-empty-title">{t("agent.empty_title")}</h3>
        <p className="ai-chat-empty-desc">{t("agent.empty_description")}</p>
        <div className="ai-chat-suggestions">
          {examples.map((question) => (
            <button
              key={question}
              onClick={() => onExampleClick(question)}
              className="ai-chat-suggestion-item"
            >
              <ArrowRight className="ai-chat-suggestion-icon" />
              <span>{question}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className="ai-chat-messages"
      role="log"
      aria-live="polite"
      aria-label={t("agent.message_list")}
    >
      {messages.map((msg) => (
        <AiAgentMessageBubble key={msg.id} message={msg} onSubmitFeedback={onSubmitFeedback} />
      ))}
      {isLoading && (
        <div className="ai-chat-typing-wrapper" role="status">
          <div className="ai-chat-typing">
            <span className="ai-chat-typing-dots">
              <span style={{ animationDelay: "0ms" }}></span>
              <span style={{ animationDelay: "150ms" }}></span>
              <span style={{ animationDelay: "300ms" }}></span>
            </span>
          </div>
        </div>
      )}
      <div ref={bottomRef} />
    </div>
  );
};
